import React, { useEffect, useReducer, useState } from 'react';
import { Grid } from 'semantic-ui-react';
import GridColumnInputWrapper from '../../../../common/GridColumnInputWrapper';
import CommonSelectInputWrapper from '../../../../common/CommonSelectInputWrapper';
import {
  HIGH_VOLATILITY,
  LOW_VOLATILITY,
  MEDIUM_VOLATILITY,
  RISK_TYPE_DUST_TENDENCY_CODE,
  RISK_TYPE_VOLATILITY_CODE,
} from '../../../../../constants';
import { AccordionWrapper, InputWrapper } from 'react-lib/dist/components-es';
import { getOptions, getRiskOptions } from '../../../../../utils';

import IExposedPersonListContainer from './IExposedPersonListContainer';
import { risksActions } from '../../../../../behavior/risks';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

type Props = {
  stateChange: number;
  values: any;
  setFieldValue: (arg1: string, arg2: string | null) => void;
  chemRiskCalculation: () => void;
  initialData: any;
  mode: string;
  errors: any;
  validateOnChange: boolean;
  setOverridePersonState: (arg: boolean) => void;
  disabled: boolean;
};

const IChemicalRiskInhalationContainer = ({
  stateChange,
  values,
  setFieldValue,
  chemRiskCalculation,
  initialData,
  mode,
  errors,
  validateOnChange,
  setOverridePersonState,
  disabled,
}: Props) => {
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  const { i18n } = useTranslation();
  const [usageFrequencyOptions, setUsageFrequencyOptions]: any[] = useState([]);
  const [dustTendencyOptions, setDustTendencyOptions]: any[] = useState([]);
  const [volatilityOptions, setVolatilityOptions]: any[] = useState([]);

  useEffect(() => {
    initOptions();
  }, [(i18n as any).language]);

  useEffect(() => {
    onStateChange();
  }, [stateChange]);

  const onStateChange = () => {
    const stateId = values.chemicalProduct.state.id;
    if (stateId == 3) {
      values.chemicalRiskAttributes.boilingPoint = null;
      values.chemicalRiskAttributes.operatingTemperature = null;
      updateVolatility();
    } else {
      setFieldValue('chemicalRiskAttributes.dustTendency.id', null);
    }
  };
  const calculateVolatilityValue = (ot: number | null, bp: number | null) => {
    if (ot != null && bp != null) {
      const highLimit = 5 * ot + 50;
      const lowLimit = 2 * ot + 10;

      if (bp > lowLimit && bp < highLimit) {
        return MEDIUM_VOLATILITY;
      } else if (bp >= highLimit) {
        return LOW_VOLATILITY;
      } else {
        return HIGH_VOLATILITY;
      }
    }
    return null;
  };

  const updateVolatility = () => {
    const ot =
      values.chemicalRiskAttributes.operatingTemperature != null &&
      values.chemicalRiskAttributes.operatingTemperature != ''
        ? Number(values.chemicalRiskAttributes.operatingTemperature)
        : null;
    const bp =
      values.chemicalRiskAttributes.boilingPoint != null &&
      values.chemicalRiskAttributes.boilingPoint != ''
        ? Number(values.chemicalRiskAttributes.boilingPoint)
        : null;

    const volatilityValue = calculateVolatilityValue(ot, bp);

    const volatility = volatilityOptions.find((p: any) => p.value == volatilityValue);
    values.chemicalRiskAttributes.volatility.id = volatility ? volatility.value : '';
    values.chemicalRiskAttributes.volatility.value = volatility ? volatility.text : '';
    forceUpdate();
    chemRiskCalculation();
  };

  const initOptions = () => {
    const { riskList, usageFrequencyList } = initialData;
    setVolatilityOptions(
      getRiskOptions(riskList, RISK_TYPE_VOLATILITY_CODE, values.chemicalRiskAttributes.volatility)
    );

    const dustTendencyOptions = getRiskOptions(
      riskList,
      RISK_TYPE_DUST_TENDENCY_CODE,
      values.chemicalRiskAttributes.dustTendency
    );

    dustTendencyOptions.unshift({ text: 'Select', value: null, key: -1 });

    setDustTendencyOptions(dustTendencyOptions);

    const usageFrequencyOptions = getOptions(
      usageFrequencyList,
      values.chemicalRiskAttributes.usageFrequency,
      ['name']
    );

    usageFrequencyOptions.unshift({ text: 'Select', value: null, key: -1 });

    setUsageFrequencyOptions(usageFrequencyOptions);

    forceUpdate();
  };

  const getInhalationFieldsContainer = () => {
    return (
      <AccordionWrapper
        closable={false}
        accordionTitle=""
        className=""
        render={getInhalationFields}
      />
    );
  };

  const isStateAny = (anyIds: number[]) => {
    const { chemicalProduct } = values;
    const state = chemicalProduct ? chemicalProduct.state : { id: null };
    return state && anyIds.includes(state.id);
  };

  const getChemicalRiskInformationContainerContent = () => {
    return (
      <Grid stackable columns={2}>
        <Grid.Column width={8}>{getInhalationFieldsContainer()}</Grid.Column>
        <Grid.Column width={8}>
          <IExposedPersonListContainer
            values={values}
            mode={mode}
            validateOnChange={validateOnChange}
            errors={errors}
            setOverridePersonState={setOverridePersonState}
          />
        </Grid.Column>
      </Grid>
    );
  };

  const getInhalationFields = () => {
    const { chemicalRiskAttributes } = values;
    if (!chemicalRiskAttributes.dustTendency) chemicalRiskAttributes.dustTendency = {};
    if (!chemicalRiskAttributes.volatility) chemicalRiskAttributes.volatility = {};
    if (chemicalRiskAttributes.usageFrequency && !chemicalRiskAttributes.usageFrequency.id)
      chemicalRiskAttributes.usageFrequency = null;
    chemicalRiskAttributes.chemicalProductStateId = values.chemicalProduct.state.id;

    return (
      <Grid stackable columns={3} verticalAlign="bottom">
        <GridColumnInputWrapper
          value={chemicalRiskAttributes.boilingPoint ? chemicalRiskAttributes.boilingPoint : ''}
          name="chemicalRiskAttributes.boilingPoint"
          label="inventory.details.page.chemical.risk.inf.container.boiling.point.input"
          type="number"
          disabled={disabled || !isStateAny([2])}
          onChange={updateVolatility}
          setFieldValue={setFieldValue}
          errors={errors}
          specialRequiredCondition={
            isStateAny([2]) && chemicalRiskAttributes.hasChemRiskCalculation
          }
          render={(props: any) => (
            <InputWrapper
              {...props}
              inputLabel={{
                basic: true,
                content: 'input.label.celsius',
              }}
              inputLabelPosition={'right'}
            />
          )}
        />
        <GridColumnInputWrapper
          value={
            chemicalRiskAttributes.operatingTemperature
              ? chemicalRiskAttributes.operatingTemperature
              : ''
          }
          name="chemicalRiskAttributes.operatingTemperature"
          label="inventory.details.page.chemical.risk.inf.container.operating.temp.input"
          type="number"
          disabled={disabled || !isStateAny([2])}
          onChange={updateVolatility}
          setFieldValue={setFieldValue}
          errors={errors}
          specialRequiredCondition={
            isStateAny([2]) && chemicalRiskAttributes.hasChemRiskCalculation
          }
          render={(props: any) => (
            <InputWrapper
              {...props}
              inputLabel={{
                basic: true,
                content: 'input.label.celsius',
              }}
              inputLabelPosition={'right'}
            />
          )}
        />
        <GridColumnInputWrapper
          value={chemicalRiskAttributes.volatility.id || ''}
          name="chemicalRiskAttributes.volatility.id"
          label="inventory.details.page.chemical.risk.inf.container.volatility.input"
          options={volatilityOptions}
          disabled={true}
          setFieldValue={setFieldValue}
          errors={errors}
          render={(props: any) => <CommonSelectInputWrapper {...props} />}
        />
        <GridColumnInputWrapper
          value={chemicalRiskAttributes.dustTendency.id || ''}
          name="chemicalRiskAttributes.dustTendency.id"
          label="inventory.details.page.chemical.risk.inf.container.dust.tendency.input"
          disabled={disabled || !isStateAny([3])}
          onChange={chemRiskCalculation}
          options={dustTendencyOptions}
          setFieldValue={setFieldValue}
          errors={errors}
          placeholder="select.input.default.placeholder"
          specialRequiredCondition={
            isStateAny([3]) && chemicalRiskAttributes.hasChemRiskCalculation
          }
          render={(props: any) => <CommonSelectInputWrapper {...props} />}
        />
        <GridColumnInputWrapper
          value={
            chemicalRiskAttributes.usageFrequency ? chemicalRiskAttributes.usageFrequency.id : null
          }
          name="chemicalRiskAttributes.usageFrequency.id"
          label="inventory.details.page.chemical.risk.inf.container.usage.frequency.input"
          disabled={disabled}
          options={usageFrequencyOptions}
          propUsedInChanges="name"
          setFieldValue={setFieldValue}
          errors={errors}
          placeholder="select.input.default.placeholder"
          render={(props: any) => <CommonSelectInputWrapper {...props} />}
        />
      </Grid>
    );
  };

  return (
    <AccordionWrapper
      collapsed={!values.chemicalRiskAttributes.hasChemRiskCalculation}
      accordionTitle="inventory.details.page.chemical.risk.inhalation.container.title"
      className="containerReducedWhiteSpace"
      render={getChemicalRiskInformationContainerContent}
    />
  );
};

const mapStateToProps = (state: any) => ({
  stateChange: state.chemicalProducts.stateChange,
  chemRiskCalculationToggled: state.inventories.chemRiskCalculationToggled,
});

const mapDispatchToProps = (dispatch: any) => ({
  chemRiskCalculation: () => dispatch(risksActions.chemRiskCalculation()),
});

export default connect(mapStateToProps, mapDispatchToProps)(IChemicalRiskInhalationContainer);
